'use client'

/**
 * SideBar — v5-spec collapsible navigation sidebar.
 *
 * Ported from Elements v5 src/core/side-bar/. Uses native `<details>`/`<summary>`
 * for disclosure, CSS-driven width transitions, and a context-based state model.
 *
 * Usage:
 * ```tsx
 * <SideBar footer={<SideBar.CollapseButton />}>
 *   <SideBar.MenuList>
 *     <SideBar.MenuItem icon={<HomeIcon />} href="/dashboard" aria-current="page">
 *       Dashboard
 *     </SideBar.MenuItem>
 *     <SideBar.MenuGroup
 *       summary={<SideBar.MenuGroupSummary icon={<BuildingIcon />}>Properties</SideBar.MenuGroupSummary>}
 *     >
 *       <SideBar.Submenu>
 *         <SideBar.SubmenuItem href="/properties/sale" aria-current={false}>For sale</SideBar.SubmenuItem>
 *         <SideBar.SubmenuItem href="/properties/rent" aria-current="page">For rent</SideBar.SubmenuItem>
 *       </SideBar.Submenu>
 *     </SideBar.MenuGroup>
 *   </SideBar.MenuList>
 * </SideBar>
 * ```
 */

import * as React from 'react'
import { ChevronDownIcon, MenuCollapseIcon } from '@/src/icons'
import { cn } from '@/registry/reapit/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/registry/reapit/ui/tooltip'

// ============================================================================
// State management
// ============================================================================

type SideBarState = 'collapsed' | 'expanded'

interface SideBarContextValue {
  id: string
  expand: () => void
  state: SideBarState
  setState: (state: SideBarState) => void
  toggle: () => void
}

const SideBarContext = React.createContext<SideBarContextValue | undefined>(undefined)

function useSideBarContext(): SideBarContextValue {
  const context = React.useContext(SideBarContext)
  if (!context) {
    throw new Error('useSideBarContext must be used within a SideBar')
  }
  return context
}

/**
 * Manages the collapsed/expanded state of the sidebar and provides setters.
 */
function useSideBarState(initialState: SideBarState | (() => SideBarState) = 'expanded') {
  const [state, setState] = React.useState(initialState)

  const expand = React.useCallback(() => setState('expanded'), [])
  const toggle = React.useCallback(() => setState((prev) => (prev === 'collapsed' ? 'expanded' : 'collapsed')), [])

  return React.useMemo(() => ({ expand, state, setState, toggle }), [expand, state, setState, toggle])
}

// ============================================================================
// Media match effect — auto collapse/expand based on viewport
// ============================================================================

/** Returns which state the sidebar should have based on the viewport width. */
function determineSideBarStateFromViewport(): SideBarState {
  if (typeof window === 'undefined') return 'expanded'
  return window.matchMedia('(min-width: 1440px)').matches ? 'expanded' : 'collapsed'
}

/**
 * Listens for viewport width changes and updates sidebar state accordingly.
 * At >= 1440px (widescreen) the sidebar expands; below that it collapses.
 */
function useSideBarMatchMediaEffect(sideBar: { setState: (state: SideBarState) => void }) {
  React.useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia('(min-width: 1440px)')
    const handler = () => sideBar.setState(determineSideBarStateFromViewport())

    handler() // set initial state
    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [sideBar.setState])
}

// ============================================================================
// Controller — observes aria-current/data-is-active and closes irrelevant groups
// ============================================================================

function useSideBarController(sideBarState: SideBarState) {
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!ref.current) return
    if (sideBarState === 'collapsed') return

    const element = ref.current
    const allMenuGroups = element.querySelectorAll('details')

    const observer = new MutationObserver(() => {
      allMenuGroups.forEach((menuGroup) => {
        if (menuGroup.open) {
          menuGroup.open = shouldBeOpen(menuGroup)
        }
      })
    })

    observer.observe(element, {
      attributeFilter: ['aria-current', 'data-is-active'],
      subtree: true,
    })

    return () => observer.disconnect()
  }, [sideBarState])

  return ref
}

// ============================================================================
// Keyboard navigation
// ============================================================================

const MENU_ITEM_CLASS = 'side-bar-menu-item'
const SUBMENU_ITEM_CLASS = 'side-bar-submenu-item'

function useSideBarKeyboardNavigation(): React.KeyboardEventHandler<HTMLDivElement> {
  return React.useCallback((event) => {
    if (event.defaultPrevented) return

    // Space activates links (Enter is handled natively for <a> and <summary>)
    if (event.key === ' ' && event.target instanceof HTMLAnchorElement) {
      event.target.click()
      return
    }

    // Arrow key navigation
    const key = event.key
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) {
      const selectors = `.${MENU_ITEM_CLASS}, details[open] .${SUBMENU_ITEM_CLASS}`
      const commonAncestor = event.currentTarget
      const elements = commonAncestor.querySelectorAll(selectors)
      let activeIndex: number | null = null
      elements.forEach((item, index) => {
        if (item === document.activeElement) activeIndex = index
      })

      if (activeIndex === null) return
      if (activeIndex === 0 && (key === 'ArrowUp' || key === 'ArrowLeft')) return
      if (activeIndex === elements.length - 1 && (key === 'ArrowDown' || key === 'ArrowRight')) return

      event.preventDefault()
      const nextIndex = key === 'ArrowUp' || key === 'ArrowLeft' ? activeIndex - 1 : activeIndex + 1
      const elementToFocus = elements.item(nextIndex)
      if (elementToFocus instanceof HTMLElement) {
        elementToFocus.focus()
        elementToFocus.scrollIntoView({ block: 'nearest' })
      }
      return
    }

    // Escape closes the containing menu group
    if (event.key === 'Escape' && event.target instanceof HTMLElement) {
      const detailsElement = event.target.closest('details')
      const currentPageElement = detailsElement?.querySelector('[aria-current="page"]')
      const summaryElement = detailsElement?.querySelector('summary')

      event.preventDefault()
      summaryElement?.focus()
      summaryElement?.scrollIntoView({ block: 'nearest' })

      if (detailsElement && !currentPageElement) {
        detailsElement.open = false
      }
    }
  }, [])
}

// ============================================================================
// shouldBeOpen utility — determines if a <details> menu group should be open
// ============================================================================

function shouldBeOpen(detailsElement: HTMLDetailsElement): boolean {
  return detailsElement.dataset.isActive === 'true' || !!detailsElement.querySelector('[aria-current="page"]')
}

// ============================================================================
// MenuGroup controller — opens/closes individual menu groups
// ============================================================================

function useMenuGroupController(sideBarState: SideBarState): React.RefObject<HTMLDetailsElement | null> {
  const ref = React.useRef<HTMLDetailsElement>(null)

  React.useLayoutEffect(() => {
    if (!ref.current) return

    if (sideBarState === 'collapsed') {
      ref.current.open = false
    } else {
      if (shouldBeOpen(ref.current)) {
        ref.current.open = true
      }

      const detailsElement = ref.current

      // Observe data-is-active changes on the details element itself
      const dataIsActiveObserver = new MutationObserver(() => {
        detailsElement.open = shouldBeOpen(detailsElement)
      })
      dataIsActiveObserver.observe(detailsElement, { attributeFilter: ['data-is-active'] })

      // Observe aria-current changes on descendants
      const ariaCurrentObserver = new MutationObserver(() => {
        detailsElement.open = shouldBeOpen(detailsElement)
      })
      ariaCurrentObserver.observe(detailsElement, { subtree: true, attributeFilter: ['aria-current'] })

      return () => {
        dataIsActiveObserver.disconnect()
        ariaCurrentObserver.disconnect()
      }
    }
  }, [sideBarState])

  return ref
}

// ============================================================================
// MenuGroupLabelId context — wires aria-labelledby between details and summary
// ============================================================================

const MenuGroupLabelIdContext = React.createContext<string | null>(null)

function useMenuGroupLabelIdContext(): string {
  const id = React.useContext(MenuGroupLabelIdContext)
  if (!id) {
    throw new Error('MenuGroupLabelIdContext must be provided by SideBar.MenuGroup')
  }
  return id
}

// ============================================================================
// Styled class constants — matching v5 visual spec using Tailwind utilities
// ============================================================================

// v5: grid layout, icon + label, gap 12px, padding 8px, rounded-lg, hover #e5e9ed
// Selected: purple text + purple icon, NO background fill
const menuItemClasses = cn(
  MENU_ITEM_CLASS,
  'grid grid-cols-[auto_1fr] items-center gap-3 p-2 w-full',
  'no-underline rounded-lg',
  'hover:bg-[var(--sidebar-accent)] focus-visible:outline-2 focus-visible:outline-[var(--sidebar-ring)] focus-visible:outline-offset-1',
  // Label styling
  '[&>[data-slot=label]]:col-start-2 [&>[data-slot=label]]:truncate [&>[data-slot=label]]:py-0.5',
  '[&>[data-slot=label]]:text-sm [&>[data-slot=label]]:font-normal [&>[data-slot=label]]:text-sidebar-foreground',
  // Icon styling
  '[&>[data-slot=icon]]:col-start-1 [&>[data-slot=icon]]:inline-flex [&>[data-slot=icon]]:items-center',
  '[&>[data-slot=icon]]:box-content [&>[data-slot=icon]]:p-0.5',
  '[&>[data-slot=icon]]:w-5 [&>[data-slot=icon]]:h-5',
  '[&>[data-slot=icon]]:text-[var(--icon-neutral-default)]',
  '[&>[data-slot=icon]_svg]:w-5 [&>[data-slot=icon]_svg]:h-5',
  // Selected state — purple text and icon only, NO background
  '[&[aria-current=page]>[data-slot=label]]:text-primary [&[aria-current=page]>[data-slot=label]]:font-medium',
  '[&[aria-current=page]>[data-slot=icon]]:text-primary',
)

// v5: submenu item — no icon, 44px left padding (indent below icon column), height 32px
const submenuItemClasses = cn(
  SUBMENU_ITEM_CLASS,
  'flex items-center text-start w-full',
  'no-underline rounded-none h-8',
  'py-0 pr-3 pl-[44px]',
  'hover:bg-[var(--sidebar-accent)] focus-visible:outline-2 focus-visible:outline-[var(--sidebar-ring)] focus-visible:outline-offset-1',
  '[&>[data-slot=label]]:truncate [&>[data-slot=label]]:text-sm [&>[data-slot=label]]:font-normal [&>[data-slot=label]]:text-sidebar-foreground',
  '[&[aria-current=page]>[data-slot=label]]:text-primary [&[aria-current=page]>[data-slot=label]]:font-medium',
)

// ============================================================================
// SideBar root
// ============================================================================

export namespace SideBar {
  export interface Props extends Omit<React.ComponentProps<'nav'>, 'children'> {
    /** The sidebar's menu items. Typically a SideBar.MenuList. */
    children: React.ReactNode
    /** The sidebar's footer. Should typically be a SideBar.CollapseButton. */
    footer: React.ReactNode
    /** The width of the sidebar when expanded. Default: 256px. Min: 192px, Max: 256px. */
    width?: number
  }
}

function SideBarRoot({
  'aria-label': ariaLabel,
  children,
  footer,
  id,
  width = 256,
  className,
  ...props
}: SideBar.Props) {
  const sideBarId = id ?? React.useId()
  const sideBar = useSideBarState(() => determineSideBarStateFromViewport())
  const handleKeyboardNavigation = useSideBarKeyboardNavigation()
  const ref = useSideBarController(sideBar.state)

  useSideBarMatchMediaEffect(sideBar)

  const contextValue = React.useMemo(() => ({ id: sideBarId, ...sideBar }), [sideBarId, sideBar])

  const isCollapsed = sideBar.state === 'collapsed'

  return (
    <nav
      {...props}
      aria-label={ariaLabel ?? 'Sidebar navigation'}
      data-state={sideBar.state}
      id={sideBarId}
      className={cn(
        'flex flex-col items-start justify-between gap-3 h-full overflow-auto',
        'border-r border-[var(--sidebar-border)] bg-[var(--sidebar)]',
        // Scroll padding: top matches body padding, bottom matches footer height
        '[scroll-padding-block:0.75rem_4rem]',
        // Collapsed: icon-only width (64px)
        isCollapsed && 'max-w-16',
        // Expanded: respect configured width, clamped 192px-256px
        !isCollapsed && 'min-w-48 max-w-64',
        className,
      )}
      style={
        {
          '--side-bar-width': `${width}px`,
          width: `${width}px`,
        } as React.CSSProperties
      }
    >
      <SideBarContext.Provider value={contextValue}>
        <div
          ref={ref}
          onClick={sideBar.expand}
          onKeyDown={handleKeyboardNavigation}
          className="w-full pt-3 px-3 bg-[var(--sidebar)]"
        >
          {children}
        </div>
        <div className="sticky bottom-0 pb-3 px-3 w-full bg-[var(--sidebar)]">{footer}</div>
      </SideBarContext.Provider>
    </nav>
  )
}

// ============================================================================
// SideBar.MenuList
// ============================================================================

export namespace SideBarMenuList {
  export interface Props extends React.ComponentProps<'ul'> {}
}

function SideBarMenuList({ children, className, ...rest }: SideBarMenuList.Props) {
  return (
    <ul {...rest} className={cn('list-none flex flex-col gap-2 m-0 p-0 pb-2 w-full', className)}>
      {children}
    </ul>
  )
}

// ============================================================================
// SideBar.MenuItem
// ============================================================================

export namespace SideBarMenuItem {
  export interface Props extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
    /** Whether this item represents the current page. */
    'aria-current': 'page' | false
    /** The label of the menu item. */
    children: React.ReactNode
    /** The URL to navigate to. */
    href: string
    /** The icon to display. */
    icon: React.ReactNode
  }
}

function SideBarMenuItemInner({
  'aria-current': ariaCurrent,
  children,
  className,
  icon,
  ...rest
}: SideBarMenuItem.Props) {
  const sideBar = useSideBarContext()
  const isCollapsed = sideBar.state === 'collapsed'

  const link = (
    <a {...rest} aria-current={ariaCurrent || undefined} className={cn(menuItemClasses, className)}>
      <span data-slot="icon" aria-hidden>
        {icon}
      </span>
      {!isCollapsed && <span data-slot="label">{children}</span>}
    </a>
  )

  if (isCollapsed) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right">{children}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return link
}

function SideBarMenuListItem({ children, ...props }: SideBarMenuItem.Props) {
  return (
    <li className="block">
      <SideBarMenuItemInner {...props}>{children}</SideBarMenuItemInner>
    </li>
  )
}

// ============================================================================
// SideBar.MenuGroup
// ============================================================================

export namespace SideBarMenuGroup {
  export interface Props extends React.DetailsHTMLAttributes<HTMLDetailsElement> {
    /** Typically a single SideBar.Submenu component. */
    children: React.ReactNode
    /** Force the group to be active even when no submenu item is current. */
    isActive?: boolean
    /** The summary/main item. Should be a SideBar.MenuGroupSummary. */
    summary: React.ReactNode
  }
}

function SideBarMenuGroupInner({
  'aria-labelledby': ariaLabelledBy,
  children,
  className,
  isActive,
  summary,
  ...rest
}: SideBarMenuGroup.Props) {
  const labelId = ariaLabelledBy ?? React.useId()
  const sideBar = useSideBarContext()
  const ref = useMenuGroupController(sideBar.state)

  return (
    <details
      {...rest}
      aria-labelledby={labelId}
      className={cn(
        'group/menu-group rounded-lg w-full',
        // Active/open/has-current background
        'open:bg-secondary [&[data-is-active=true]]:bg-secondary [&:has([aria-current=page])]:bg-secondary',
        className,
      )}
      data-is-active={isActive || undefined}
      ref={ref}
    >
      <MenuGroupLabelIdContext.Provider value={labelId}>{summary}</MenuGroupLabelIdContext.Provider>
      {children}
    </details>
  )
}

function SideBarMenuListGroup({ children, ...props }: SideBarMenuGroup.Props) {
  return (
    <li className="block">
      <SideBarMenuGroupInner {...props}>{children}</SideBarMenuGroupInner>
    </li>
  )
}

// ============================================================================
// SideBar.MenuGroupSummary
// ============================================================================

export namespace SideBarMenuGroupSummary {
  export interface Props extends React.HTMLAttributes<HTMLElement> {
    /** The label for the menu group. */
    children: React.ReactNode
    /** The icon to display. */
    icon: React.ReactNode
  }
}

function SideBarMenuGroupSummary({ children, className, icon, onClick, ...props }: SideBarMenuGroupSummary.Props) {
  const labelId = useMenuGroupLabelIdContext()
  const sideBar = useSideBarContext()
  const isCollapsed = sideBar.state === 'collapsed'

  // Prevent closing the group if it's active (has a current page descendant)
  const handleClick: React.MouseEventHandler<HTMLElement> = React.useCallback(
    (event) => {
      onClick?.(event)
      const detailsElement = event.currentTarget.closest('details')
      if (detailsElement && shouldBeOpen(detailsElement)) {
        event.preventDefault()
      }
    },
    [onClick],
  )

  const summary = (
    <summary
      {...props}
      id={labelId}
      className={cn(
        menuItemClasses,
        // Override grid to add dropdown column
        'grid-cols-[auto_1fr_auto] cursor-pointer overflow-hidden',
        // Remove default marker
        '[list-style:none] [&::-webkit-details-marker]:hidden',
        // Active state styling — purple text + icon when parent details is active or has current page
        'group-data-[is-active=true]/menu-group:[&>[data-slot=icon]]:text-primary',
        'group-data-[is-active=true]/menu-group:[&>[data-slot=label]]:text-primary group-data-[is-active=true]/menu-group:[&>[data-slot=label]]:font-medium',
        '[[data-is-active=true]>&>[data-slot=icon]]:text-primary',
        '[[data-is-active=true]>&>[data-slot=label]]:text-primary [[data-is-active=true]>&>[data-slot=label]]:font-medium',
        className,
      )}
      onClick={handleClick}
    >
      <span data-slot="icon" aria-hidden>
        {icon}
      </span>
      {!isCollapsed && (
        <>
          <span data-slot="label">{children}</span>
          <span
            data-slot="dropdown"
            aria-hidden
            className={cn(
              'col-start-3 inline-flex items-center box-content p-1',
              'w-4 h-4 text-[var(--icon-neutral-default)]',
              '[&_svg]:w-4 [&_svg]:h-4',
              // Rotate when parent details is open
              'group-open/menu-group:rotate-180',
            )}
            style={{
              transition: 'transform 150ms ease',
            }}
          >
            <ChevronDownIcon size="sm" />
          </span>
        </>
      )}
    </summary>
  )

  if (isCollapsed) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>{summary}</TooltipTrigger>
          <TooltipContent side="right">{children}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return summary
}

// ============================================================================
// SideBar.Submenu
// ============================================================================

export namespace SideBarSubmenu {
  export interface Props extends React.ComponentProps<'ul'> {
    /** A collection of SideBar.SubmenuItem components. */
    children: React.ReactNode
  }
}

function SideBarSubmenu({ children, className, ...rest }: SideBarSubmenu.Props) {
  return (
    <ul {...rest} className={cn('list-none flex flex-col gap-1 m-0 p-0 pb-2', className)}>
      {children}
    </ul>
  )
}

// ============================================================================
// SideBar.SubmenuItem
// ============================================================================

export namespace SideBarSubmenuItem {
  export interface Props extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'aria-current'> {
    /** Whether this item represents the current page. */
    'aria-current': 'page' | false
    /** The label of the menu item. */
    children: React.ReactNode
    /** The URL to navigate to. */
    href: string
  }
}

function SideBarSubmenuItemInner({
  'aria-current': ariaCurrent,
  children,
  className,
  ...rest
}: SideBarSubmenuItem.Props) {
  return (
    <a {...rest} aria-current={ariaCurrent || undefined} className={cn(submenuItemClasses, className)}>
      <span data-slot="label">{children}</span>
    </a>
  )
}

function SideBarSubmenuListItem({ children, ...props }: SideBarSubmenuItem.Props) {
  return (
    <li className="block">
      <SideBarSubmenuItemInner {...props}>{children}</SideBarSubmenuItemInner>
    </li>
  )
}

// ============================================================================
// SideBar.CollapseButton
// ============================================================================

export namespace SideBarCollapseButton {
  export interface Props extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'aria-expanded' | 'aria-controls' | 'children'
  > {}
}

function SideBarCollapseButton({ onClick, className, ...props }: SideBarCollapseButton.Props) {
  const sideBar = useSideBarContext()
  const label = sideBar.state === 'expanded' ? 'Collapse' : 'Expand'
  const isCollapsed = sideBar.state === 'collapsed'

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = React.useCallback(
    (event) => {
      onClick?.(event)
      if (event.defaultPrevented) return
      sideBar.toggle()
    },
    [onClick, sideBar.toggle],
  )

  const button = (
    <button
      {...props}
      aria-controls={sideBar.id}
      aria-expanded={sideBar.state === 'expanded'}
      onClick={handleClick}
      type="button"
      className={cn(
        'flex items-center gap-3 p-2 w-full cursor-pointer',
        'bg-[var(--sidebar)] border-none rounded-lg',
        'hover:bg-[var(--sidebar-accent)]',
        'focus-visible:outline-2 focus-visible:outline-[var(--sidebar-ring)] focus-visible:outline-offset-1',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'flex items-center p-0.5',
          'text-[var(--icon-neutral-default)]',
          '[&_svg]:w-5 [&_svg]:h-5',
          isCollapsed && 'rotate-180',
        )}
      >
        <MenuCollapseIcon />
      </span>
      {!isCollapsed && (
        <span className="overflow-hidden text-ellipsis text-sm font-normal text-sidebar-foreground">{label}</span>
      )}
    </button>
  )

  if (isCollapsed) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side="right">{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return button
}

// ============================================================================
// Compose SideBar namespace
// ============================================================================

const SideBar = Object.assign(SideBarRoot, {
  CollapseButton: SideBarCollapseButton,
  MenuList: SideBarMenuList,
  MenuItem: SideBarMenuListItem,
  MenuGroup: SideBarMenuListGroup,
  MenuGroupSummary: SideBarMenuGroupSummary,
  Submenu: SideBarSubmenu,
  SubmenuItem: SideBarSubmenuListItem,
})

export { SideBar, useSideBarContext }
export type { SideBarState }
